import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GodModeUser {
  id: string;
  email: string;
  full_name: string;
}

interface GodModeAuthContextType {
  user: GodModeUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const GodModeAuthContext = createContext<GodModeAuthContextType | null>(null);

const STORAGE_KEY = 'godmode_token';
const USER_KEY = 'godmode_user';

export const useGodModeAuth = () => {
  const context = useContext(GodModeAuthContext);
  if (!context) {
    throw new Error('useGodModeAuth deve ser usado dentro de GodModeAuthProvider');
  }
  return context;
};

interface GodModeAuthProviderProps {
  children: ReactNode;
}

export const GodModeAuthProvider: React.FC<GodModeAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<GodModeUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Verify token on mount
  useEffect(() => {
    const verifyStoredToken = async () => {
      const storedToken = localStorage.getItem(STORAGE_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('godmode-auth/verify', {
          method: 'GET',
          headers: {
            'x-godmode-token': storedToken,
          },
        });

        if (error || !data?.valid) {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(USER_KEY);
          setToken(null);
          setUser(null);
        } else {
          setToken(storedToken);
          setUser(data.user);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(USER_KEY);
      } finally {
        setLoading(false);
      }
    };

    verifyStoredToken();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('godmode-auth/login', {
        body: { email, password },
      });

      if (error) {
        const errorMessage = error.message || 'Erro ao fazer login';
        setError(errorMessage);
        setLoading(false);
        return { success: false, error: errorMessage };
      }

      if (data?.error) {
        setError(data.error);
        setLoading(false);
        return { success: false, error: data.error };
      }

      if (data?.token && data?.user) {
        localStorage.setItem(STORAGE_KEY, data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setToken(data.token);
        setUser(data.user);
        setLoading(false);
        return { success: true };
      }

      setError('Resposta inválida do servidor');
      setLoading(false);
      return { success: false, error: 'Resposta inválida do servidor' };
    } catch (err: any) {
      const errorMessage = err?.message || 'Erro de conexão';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await supabase.functions.invoke('godmode-auth/logout', {
          headers: {
            'x-godmode-token': token,
          },
        });
      }
    } catch {
      // Ignore errors on logout
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(USER_KEY);
      setToken(null);
      setUser(null);
    }
  }, [token]);

  const value: GodModeAuthContextType = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!token && !!user,
  };

  return (
    <GodModeAuthContext.Provider value={value}>
      {children}
    </GodModeAuthContext.Provider>
  );
};
